import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/dialog';
import { useDeleteAccount } from '../hooks/use-delete-account';
import { useAuthStore } from '@/lib/stores/auth.store';
import { deleteAccountSchema, type DeleteAccountFormData } from '@/lib/validations/settings.schema';
import { AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';

export function DeleteAccountContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { logout } = useAuthStore();
  const { mutate: deleteAccount, isPending, isError, error } = useDeleteAccount();

  const form = useForm<DeleteAccountFormData>({
    resolver: zodResolver(deleteAccountSchema),
    defaultValues: {
      password: '',
      reason: '',
      confirmation: '',
    },
  });

  const confirmationValue = form.watch('confirmation');
  const isConfirmationValid = confirmationValue === 'DELETE';

  const handleSubmit = (data: DeleteAccountFormData) => {
    if (data.confirmation !== 'DELETE') {
      form.setError('confirmation', { message: 'settings.deleteAccount.confirmationError' });
      return;
    }
    deleteAccount(
      {
        password: data.password,
        reason: data.reason,
      },
      {
        onSuccess: () => {
          logout();
          navigate('/login');
        },
      }
    );
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <Trash2 className="size-5" />
          {t('settings.deleteAccount.title')}
        </CardTitle>
        <CardDescription>{t('settings.deleteAccount.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            {t('settings.deleteAccount.warning')}
          </AlertDescription>
        </Alert>

        <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground mb-6">
          <li>{t('settings.deleteAccount.consequence1')}</li>
          <li>{t('settings.deleteAccount.consequence2')}</li>
          <li>{t('settings.deleteAccount.consequence3')}</li>
          <li>{t('settings.deleteAccount.consequence4')}</li>
        </ul>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="destructive">
              {t('settings.deleteAccount.startDeletion')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('settings.deleteAccount.confirmTitle')}</DialogTitle>
              <DialogDescription>
                {t('settings.deleteAccount.confirmDescription')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              {isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {(error as Error)?.message || t('settings.deleteAccount.error')}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="reason">{t('settings.deleteAccount.reason')}</Label>
                <Textarea
                  id="reason"
                  placeholder={t('settings.deleteAccount.reasonPlaceholder')}
                  {...form.register('reason')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('settings.deleteAccount.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="pr-10"
                    {...form.register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {t(form.formState.errors.password.message ?? '')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmation">
                  {t('settings.deleteAccount.confirmationLabel')}
                </Label>
                <Input
                  id="confirmation"
                  placeholder="DELETE"
                  {...form.register('confirmation')}
                />
                {form.formState.errors.confirmation && (
                  <p className="text-sm text-destructive">
                    {t(form.formState.errors.confirmation.message ?? '')}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {t('settings.deleteAccount.confirmationHint')}
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="destructive" disabled={isPending || !isConfirmationValid}>
                  {isPending ? t('settings.deleteAccount.deleting') : t('settings.deleteAccount.delete')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
